/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {ActionButton, ActionButtonSecondary, Error} from 'screens/commons';

import {DigioInitialization, PartnerKyc, ClientKyc} from './components';
import {KycScreenMapping, KycFlowMapping} from 'types';
import IMAGES from 'constants/Images';
import useMSClarity from 'hooks/useMSClarity';
import {ProgressBar} from 'components/ProgressBar';
import {SuggestionBox} from './components/ClientKyc/NewClientKyc';

const propTypes = {
  requestId: PropTypes.string.isRequired,
  identifier: PropTypes.string.isRequired,
  tokenId: PropTypes.string.isRequired,
  updateStatus: PropTypes.func.isRequired,
  kycData: PropTypes.object.isRequired,
  handleErrorState: PropTypes.func,
  handleDigioInitialization: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  mountDigioComponent: PropTypes.bool.isRequired,
  handleLoading: PropTypes.func.isRequired,
  setMountDigioComponent: PropTypes.func.isRequired,
  handleGoBack: PropTypes.func.isRequired,
};

const DigioFlow = ({
  handleDigioInitialization,
  isLoading,
  isError,
  mountDigioComponent,
  requestId,
  updateStatus,
  tokenId,
  identifier,
  handleLoading,
  setMountDigioComponent,
  kycData,
  handleErrorState,
  handleGoBack,
}) => {
  const {sendClarityEvent} = useMSClarity();

  let kycComponent = <></>;

  switch (kycData.flow) {
    case 'PARTNER':
      kycComponent = (
        <PartnerKyc
          kycData={kycData}
          handleDigioInitialization={handleDigioInitialization}
          handleGoBack={handleGoBack}
        />
      );
      break;

    default:
      kycComponent = (
        <ClientKyc
          kycData={kycData}
          handleDigioInitialization={handleDigioInitialization}
          handleGoBack={handleGoBack}
        />
      );
      break;
  }

  const {stage, flow} = kycData;
  const showGoBackButton =
    (stage === KycScreenMapping.DIGILOCKER && flow === KycFlowMapping.MF_NRI) ||
    (stage === KycScreenMapping.DIGILOCKER && flow === KycFlowMapping.PARTNER);

  return (
    <Container>
      {isError ? (
        <Error
          className="error-container"
          message="Looks like something is wrong, we are looking into it, please try again after sometime."
        >
          <ActionButton
            className="mt-auto act-button"
            buttonText="Retry"
            callback={() => {
              updateStatus();
              sendClarityEvent(`RE_TRY_BTN_CLICK_${stage}_:${kycData.id}`);
            }}
          />
          {showGoBackButton && (
            <ManualProcessWrapper>
              <ActionButtonSecondary
                buttonText="Process Manually"
                callback={() => handleGoBack(flow)}
                className="mt-2 act-button"
              />
              <SuggestionBox className="mt-3">
                <img
                  src={IMAGES.infoIconImage}
                  alt="info-icon"
                  title="Choose Manual option after clicking this button"
                />
                <span className="body-small text-tertiary">
                  If you experience issues with DigiLocker, you can proceed with the manual option
                  option.
                </span>
              </SuggestionBox>
            </ManualProcessWrapper>
          )}
        </Error>
      ) : (
        <>
          {isLoading ? (
            <>
              <LoaderContainer>
                <ProgressBar
                  subheading="This process is secure and may take a few moments"
                  heading="Processing to next steps"
                  waitTimeInSec={180}
                  stopOnEndTime={true}
                  stopOnEndMsg={'Something went wrong.Please try again'}
                  showShadow={true}
                />
              </LoaderContainer>
              {mountDigioComponent && (
                <DigioInitialization
                  requestId={requestId}
                  identifier={identifier}
                  tokenId={tokenId}
                  handleLoading={handleLoading}
                  updateStatus={updateStatus}
                  setMountDigioComponent={setMountDigioComponent}
                  handleErrorState={handleErrorState}
                  kycFlow={kycData?.flow}
                  kycData={kycData}
                />
              )}
            </>
          ) : (
            kycComponent
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  background-color: ${(props) => props.theme.colors.background.primary};
  overflow-y: auto;

  .error-image {
    width: 14rem;
    margin-bottom: ${(props) => props.theme.spacing.lg};
  }

  .act-button {
    max-width: 600px !important;
    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      margin-top: 1.5rem !important;
    }
  }

  .error-container {
    height: 100% !important;
    padding: 3rem !important;
    margin-top: 3rem;
    margin-bottom: 2rem !important;

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      padding: 1.5rem !important;
      margin-top: 0 !important;
    }
  }
`;

const ManualProcessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ManualHelpText = styled.div`
  display: flex;
  align-items: flex-start;
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
  margin-top: 8px;
`;

const InfoIcon = styled.img`
  margin-right: 6px;
  margin-top: 2px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const LoaderContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: ${(props) => props.theme.spacing.md};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.sm};
  }
`;

DigioFlow.propTypes = propTypes;

export default DigioFlow;
